import { AnimatePresence, motion } from 'framer-motion'
import { CloseIcon } from '../icons'
import { formatPlatform, formatSdkPlatform } from '../../utils/platform'
import { crashStackTrace } from '../../utils/crash'

export interface AnalyticsEventRow {
  id: string
  event_type: string
  event_name?: string | null
  app_name?: string | null
  bundle_id?: string | null
  session_id?: string | null
  timestamp: string
  platform?: string | null
  device_model?: string | null
  os_version?: string | null
  app_version?: string | null
  build_number?: string | null
  sdk_version?: string | null
  sdk_platform?: string | null
  screen_resolution?: string | null
  locale?: string | null
  timezone?: string | null
  country?: string | null
  city?: string | null
  screen_name?: string | null
  properties?: Record<string, unknown> | null
  crash_reason?: string | null
}

interface EventDetailDrawerProps {
  event: AnalyticsEventRow | null
  onClose: () => void
  /** When given, the drawer offers a link to this event's session timeline. */
  onOpenSession?: (sessionId: string) => void
}

export default function EventDetailDrawer({ event, onClose, onOpenSession }: EventDetailDrawerProps) {
  return (
    <AnimatePresence>
      {event && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-white/10 bg-[#08080d]/95 shadow-2xl backdrop-blur-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 340, damping: 34 }}
            aria-label="Event detail"
          >
            <div className="flex items-start justify-between gap-4 border-b border-white/10 p-5">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-gray-500">Event Detail</p>
                <h2 className="mt-2 text-xl font-semibold text-white">{event.event_name || event.event_type}</h2>
                <p className="mt-1 text-sm text-gray-500">{new Date(event.timestamp).toLocaleString()}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="mobile-tap-target inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white"
                aria-label="Close event detail"
              >
                <CloseIcon size="md" />
              </button>
            </div>
            <div className="flex-1 space-y-5 overflow-auto p-5 custom-scrollbar">
              <DetailGrid event={event} />
              {onOpenSession && event.session_id && (
                <button
                  type="button"
                  onClick={() => onOpenSession(event.session_id as string)}
                  className="w-full rounded-xl border border-white/10 px-4 py-2.5 text-sm font-semibold text-gray-200 hover:bg-white/10 hover:text-white"
                >
                  View this session's timeline
                </button>
              )}
              {crashStackTrace(event) && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-gray-200">Stack trace</h3>
                  <pre className="max-h-[40vh] overflow-auto whitespace-pre rounded-2xl border border-white/10 bg-black/40 p-4 font-mono text-xs leading-relaxed text-gray-300 custom-scrollbar">
                    {crashStackTrace(event)}
                  </pre>
                </div>
              )}
              <div>
                <h3 className="mb-2 text-sm font-semibold text-gray-200">Properties</h3>
                <pre className="max-h-[40vh] overflow-auto rounded-2xl border border-white/10 bg-black/40 p-4 text-xs leading-relaxed text-gray-300 custom-scrollbar">
                  {JSON.stringify(event.properties ?? {}, null, 2)}
                </pre>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

function DetailGrid({ event }: { event: AnalyticsEventRow }) {
  const rows = [
    ['App', event.app_name || event.bundle_id || '—'],
    ['Bundle', event.bundle_id || '—'],
    ['Session', event.session_id || '—'],
    ['Platform', event.platform ? formatPlatform(event.platform) : '—'],
    ['Device', event.device_model || '—'],
    ['OS', event.os_version || '—'],
    ['App Version', event.app_version || '—'],
    ['Build', event.build_number || '—'],
    ['SDK', [formatSdkPlatform(event.sdk_platform), event.sdk_version].filter(Boolean).join(' ') || '—'],
    ['Screen Size', event.screen_resolution || '—'],
    ['Locale', event.locale || '—'],
    ['Timezone', event.timezone || '—'],
    ['Screen', event.screen_name || '—'],
    ['Location', [event.city, event.country].filter(Boolean).join(', ') || '—'],
    ...(event.crash_reason ? [['Crash Reason', event.crash_reason]] : []),
  ]

  return (
    <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.035] p-3">
          <dt className="text-[10px] uppercase tracking-[0.18em] text-gray-600">{label}</dt>
          <dd className="mt-1 truncate text-sm font-medium text-gray-200">{value}</dd>
        </div>
      ))}
    </dl>
  )
}
